import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import './FeedbackModal.css';
import { CHAT_API_ENDPOINTS } from "../../api/CHAT_API_ENDPOINTS";

const FeedbackModal = ({ isOpen, onClose, userId }) => {
  const { t } = useTranslation();
  
  const [rating, setRating] = useState(5); // Default to 5 stars (all selected)
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    rating: false,
    feedback: false
  });

  const validateForm = () => {
    const errors = {
      rating: rating === 0,
      feedback: feedback.trim() === ""
    };
    
    setValidationErrors(errors);
    return !errors.rating && !errors.feedback;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_ADD_FEEDBACK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // user_id: userId,
          user_id: sessionStorage.getItem('user_id') || localStorage.getItem('user_id') || userId || "",
          rating: rating,
          feedback: feedback
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }
      
      setSubmitSuccess(true);
      
      // First: Reset form and close modal
      setTimeout(() => {
        setRating(5);
        setFeedback("");
        setSubmitSuccess(false);
        onClose();
        
        // Second: Show success toast after modal closes
        setTimeout(() => {
          setShowSuccessToast(true);
          
          // Third: Hide success toast after showing it
          setTimeout(() => {
            setShowSuccessToast(false);
          }, 2000);
        }, 300); // Small delay to ensure modal is closed
        
      }, 1000); // Reduced delay for faster modal closing
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Success Toast Notification - Always rendered outside modal */}
      {showSuccessToast && (
        <div className="fixed top-6 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center feedsubmitPnc">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
            {t("feedbackSubmittedSuccessfully")}
        </div>
      )}
      
      {/* Modal - Only rendered when isOpen is true */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="w-full max-w-lg mx-4 overflow-hidden feebBorderPnc shadow-lg">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 feedHeader">
              <h2 className="FeedHeaderPnc">{t("feedback")}</h2>
              <button 
                onClick={onClose}
                className="text-black crossFeedBtnPnc"
              >
                <svg className="svgFeedPnc" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 bg-[#ffffff] contentModalPnc">
              <div className="text-center">
                <h3 className="font-medium subHeadingFeedPnc">
                  {t("helpUsShapeBetterExperience")}
                </h3>
                
                {/* Star Rating */}
                <div className="flex flex-col items-center mb-[24px] ratingBottom">
                  <div className="flex justify-center space-x-2 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => {
                          setRating(star);
                          setValidationErrors({...validationErrors, rating: false});
                        }}
                        className="focus:outline-none transform transition-transform hover:scale-110 starBorderPnc starMobViewPnc"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="36"
                          height="36"
                          fill={star <= rating ? "#FF0000" : "none"}
                          stroke={star <= rating ? "#FF0000" : "#FF0000"}
                          strokeWidth="1.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                          />
                        </svg>
                      </button>
                    ))}
                  </div>
                  {validationErrors.rating && (
                    <p className="text-red-500 text-sm mt-1"> {t("pleaseSelectRating")}</p>
                  )}
                </div>
                
                {/* Feedback Text Area */}
                <div className="mb-[24px]">
                  <textarea
                    placeholder={t("enterYourFeedback")}
                    className={`w-full border ${validationErrors.feedback ? 'border-red-500' : 'border-gray-300'} rounded-lg p-[0.7rem] focus:outline-none focus:ring-red-500 focus:border-red-500 min-h-32 resize-none`}
                    value={feedback}
                    onChange={(e) => {
                      setFeedback(e.target.value);
                      if (e.target.value.trim() !== "") {
                        setValidationErrors({...validationErrors, feedback: false});
                      }
                    }}
                  ></textarea>
                  {validationErrors.feedback && (
                    <p className="text-red-500 text-sm text-left mt-1">{t("feedbackRequired")}</p>
                  )}
                </div>
                
                {/* Error Message */}
                {submitError && (
                  <div className="mb-4 text-red-600 font-medium">
                    {submitError}
                  </div>
                )}
                
                {/* Submit Button */}
                <div className="flex justify-center">
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black submitFeedPnc disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? t("submit") : t("submit")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackModal;